// ✅ src/pages/Vehicle/VehicleTypeMaintenanceListing.jsx
// Visual design aligned with ProductionBOMListing.
// Pagination added (client-side over loaded rows).
//
// APIs:
//   GET /api/maintenanceListing/ddl
//   GET /api/maintenanceListing/list
//   GET /api/maintenanceListing/viewall
//   GET /api/maintenanceListing/subtypes/:vehicleTypeId

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import {
  FaPlus,
  FaSearch,
  FaList,
  FaChevronDown,
  FaChevronUp,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/maintenanceListing";
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

const getLS = (key, fallback = "") => {
  if (typeof window === "undefined") return fallback;
  const val = localStorage.getItem(key);
  return val !== null && val !== undefined ? val : fallback;
};

const pickFirst = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const val = obj?.[k];
    if (val !== undefined && val !== null && String(val).trim() !== "") {
      return val;
    }
  }
  return fallback;
};

const VehicleTypeMaintenanceListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const navigate = useNavigate();

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype") || "";
  const userId = getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [vehicleSubTypes, setVehicleSubTypes] = useState([]);

  const [vehicletypeid, setVehicletypeid] = useState("Select");
  const [vehiclesubtypeid, setVehiclesubtypeid] = useState("Select");
  const [showFilters, setShowFilters] = useState(true);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [rights] = useState({ canAccess: true, canAdd: true });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const fieldClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const normalizeId = (val) => {
    if (!val || val === "Select") return "%";
    return String(val);
  };

  const normalizeRow = (row) => {
    const vehicleTypeId = pickFirst(row, [
      "fld_vehicletypeid",
      "VehicleTypeId",
      "VehicleTypeID",
      "vehicletypeid",
      "vehicleTypeId",
      "id",
    ], 0);

    const vehicleSubTypeId = pickFirst(row, [
      "fld_vehicle_subtypeid",
      "fld_vehiclesubtypeid",
      "VehicleSubTypeId",
      "VehicleSubTypeID",
      "vehiclesubtypeid",
      "vehicleSubTypeId",
    ], 0);

    return {
      ...row,
      vehicleType: pickFirst(row, [
        "VehicleType",
        "Vehicletypename",
        "fld_vehicletypename",
        "vehicleType",
        "vehicletypename",
        "Vehicle Type",
      ], "-"),
      vehicleSubType: pickFirst(row, [
        "VehicleSubType",
        "Vehicle_subtypename",
        "fld_vehicle_subtypename",
        "fld_vehiclesubtypename",
        "vehiclesubtype",
        "vehicleSubType",
        "Vehicle Sub Type",
      ], "-"),
      vehicleAndSubName: pickFirst(row, [
        "VehicleandSubname",
        "VehicleAndSubName",
        "fld_vehicleandsubname",
        "vehicleAndSubName",
        "VehicleSubTypeName",
        "VehicleSubType",
      ], ""),
      vehicleTypeId,
      vehicleSubTypeId,
    };
  };

  const persistEditContext = (row = {}) => {
    const vehicleTypeId = String(row.vehicleTypeId || "");
    const vehicleSubTypeId = String(row.vehicleSubTypeId || "");
    const vehicleAndSubName = row.vehicleAndSubName || "";

    sessionStorage.setItem("editMode", row.editMode ? "true" : "false");
    sessionStorage.setItem("VehicleandSubname", vehicleAndSubName);
    sessionStorage.setItem("vehicletypeid", vehicleTypeId);
    sessionStorage.setItem("vehiclesubtypeid", vehicleSubTypeId);

    return { vehicleTypeId, vehicleSubTypeId, vehicleAndSubName };
  };

  const loadDDL = async () => {
    try {
      const r = await axios.get(`${API_BASE}/ddl`, {
        headers: commonHeaders,
      });

      if (!r.data?.success) {
        throw new Error(r.data?.message || "DDL failed");
      }

      setVehicleTypes(Array.isArray(r.data?.data?.vehicleTypes) ? r.data.data.vehicleTypes : []);
      setVehicleSubTypes(
        Array.isArray(r.data?.data?.vehicleSubTypes) ? r.data.data.vehicleSubTypes : []
      );
    } catch (err) {
      console.error(err);
      toast.error("Failed to load dropdown data!");
      setVehicleTypes([]);
      setVehicleSubTypes([]);
    }
  };

  const loadList = async () => {
    if (!branchId) {
      toast.error("branchId missing in localStorage!");
      return;
    }

    setLoading(true);
    setHasSearched(true);
    setCurrentPage(1);
    try {
      const r = await axios.get(`${API_BASE}/list`, {
        params: {
          branchId,
          vehicletypeid: normalizeId(vehicletypeid),
          vehiclesubtypeid: normalizeId(vehiclesubtypeid),
        },
        headers: commonHeaders,
      });

      const data = Array.isArray(r.data?.data) ? r.data.data : [];
      setRows(data.map(normalizeRow));
      if (data.length === 0) toast.info("No Records found");
    } catch (err) {
      console.error(err);
      toast.error("Failed to load list");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadViewAll = async () => {
    if (!branchId) {
      toast.error("branchId missing in localStorage!");
      return;
    }

    setLoading(true);
    setHasSearched(true);
    setCurrentPage(1);
    try {
      const r = await axios.get(`${API_BASE}/viewall`, {
        params: {
          branchId,
          vehicletypeid: normalizeId(vehicletypeid),
          vehiclesubtypeid: normalizeId(vehiclesubtypeid),
        },
        headers: commonHeaders,
      });

      const data = Array.isArray(r.data?.data) ? r.data.data : [];
      setRows(data.map(normalizeRow));
      if (data.length === 0) toast.info("No Records found");
    } catch (err) {
      console.error(err);
      toast.error("Failed to load view all list");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setVehicletypeid("Select");
    setVehiclesubtypeid("Select");
    setCurrentPage(1);
    loadDDL();
  };

  const onVehicleTypeChange = async (e) => {
    const val = e.target.value;
    setVehicletypeid(val);
    setVehiclesubtypeid("Select");

    if (val === "Select" || !val) {
      await loadDDL();
      return;
    }

    try {
      const r = await axios.get(`${API_BASE}/subtypes/${val}`, {
        headers: commonHeaders,
      });

      if (r.data?.success) {
        setVehicleSubTypes(Array.isArray(r.data?.data) ? r.data.data : []);
      } else {
        setVehicleSubTypes([]);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load subtypes");
      setVehicleSubTypes([]);
    }
  };

  const onEdit = (row) => {
    const raw = normalizeRow(row);
    const { vehicleTypeId, vehicleSubTypeId, vehicleAndSubName } = persistEditContext({
      ...raw,
      editMode: true,
    });

    if (!vehicleTypeId && !vehicleSubTypeId) {
      toast.error("Unable to edit. Vehicle Type / Sub Type id is missing in list data.");
      return;
    }

    navigate(
      `/vehicle/VehicleTypeMaintenanceEntry?vehicletypeid=${encodeURIComponent(
        vehicleTypeId || ""
      )}&vehiclesubtypeid=${encodeURIComponent(vehicleSubTypeId || "")}&editMode=true`,
      {
        state: {
          editMode: true,
          vehicletypeid: vehicleTypeId,
          vehiclesubtypeid: vehicleSubTypeId,
          vehicleAndSubName,
          row: raw,
        },
      }
    );
  };

  const onAddNew = () => {
    persistEditContext({
      editMode: false,
      vehicleTypeId: "",
      vehicleSubTypeId: "",
      vehicleAndSubName: "",
    });
    navigate("/vehicle/VehicleTypeMaintenanceEntry");
  };

  const totalRecords = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, currentPage, pageSize]);

  const pageStart = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const pageEnd = Math.min(currentPage * pageSize, totalRecords);

  const getVisiblePages = () => {
    const maxButtons = 5;
    if (totalPages <= maxButtons) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const half = Math.floor(maxButtons / 2);
    let start = Math.max(1, currentPage - half);
    let end = start + maxButtons - 1;

    if (end > totalPages) {
      end = totalPages;
      start = end - maxButtons + 1;
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const handlePageSizeChange = (e) => {
    const nextSize = Number(e.target.value) || DEFAULT_PAGE_SIZE;
    setPageSize(nextSize);
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
  };

  useEffect(() => {
    (async () => {
      try {
        await loadDDL();
        await loadList();
      } catch (err) {
        console.error(err);
        toast.error("Failed to initialize page");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  if (!rights.canAccess) {
    return (
      <VehicleLayoutWrapper>
        <div className="p-6">
          <div className="p-4 rounded-lg border border-red-300 bg-red-50 text-red-700">
            You don’t have rights to access this page.
          </div>
        </div>
      </VehicleLayoutWrapper>
    );
  }

  const pagerBtnClass = (active = false, disabled = false) =>
    `min-w-[36px] h-9 px-3 rounded-lg text-sm font-semibold border transition-all duration-200 flex items-center justify-center ${
      disabled
        ? isDark
          ? "opacity-40 cursor-not-allowed border-gray-600 text-gray-400"
          : "opacity-40 cursor-not-allowed border-gray-300 text-gray-400"
        : active
        ? `${buttonClass} text-white border-transparent shadow`
        : isDark
        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
        : "border-gray-300 text-gray-700 hover:bg-white"
    }`;

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE TYPE MAINTENANCE</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Type Maintenance Listing
              </h2>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {loading ? "..." : `${totalRecords}`} Records
              </span>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {rights.canAdd && (
                <button
                  onClick={onAddNew}
                  className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaPlus />
                  Add New
                </button>
              )}

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={handleClearFilters}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={loadViewAll}
                    disabled={loading}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    <FaList />
                    View All
                  </button>

                  <button
                    onClick={loadList}
                    disabled={loading}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Vehicle Type</label>
                  <select
                    value={vehicletypeid}
                    onChange={onVehicleTypeChange}
                    className={fieldClass}
                  >
                    <option value="Select">Select</option>
                    {vehicleTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Sub Type</label>
                  <select
                    value={vehiclesubtypeid}
                    onChange={(e) => setVehiclesubtypeid(e.target.value)}
                    className={fieldClass}
                  >
                    <option value="Select">Select</option>
                    {vehicleSubTypes.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {hasSearched ? "No Records found" : "Please search to view records"}
                </div>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Type</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Sub Type</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">Edit</th>
                  </tr>
                </thead>

                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {paginatedRows.map((row, idx) => {
                    const serial = pageStart + idx;
                    return (
                      <tr
                        key={`${row.vehicleTypeId}-${row.vehicleSubTypeId}-${serial}`}
                        className={`${
                          idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-white"
                            : isDark
                            ? "bg-gray-750"
                            : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {serial}
                        </td>

                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {row.vehicleType}
                        </td>

                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {row.vehicleSubType}
                        </td>

                        <td className="px-6 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => onEdit(row)}
                            className="text-yellow-500 hover:text-yellow-600 text-lg transition"
                            title="Edit"
                          >
                            ✏️
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {!loading && totalRecords > 0 && (
            <div
              className={`mt-4 flex flex-col lg:flex-row items-center justify-between gap-4 px-2 py-3 ${
                isDark ? "text-gray-300" : "text-gray-700"
              }`}
            >
              <div className="flex items-center gap-3 flex-wrap text-sm">
                <span>
                  Showing <span className="font-semibold">{pageStart}</span>–
                  <span className="font-semibold">{pageEnd}</span> of{" "}
                  <span className="font-semibold">{totalRecords}</span> records
                </span>

                <label className="flex items-center gap-2">
                  <span>Rows per page</span>
                  <select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    className={`px-3 py-1.5 rounded-lg border text-sm ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={pagerBtnClass(false, currentPage === 1)}
                  title="Previous"
                >
                  <FaChevronLeft />
                </button>

                {getVisiblePages()[0] > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => goToPage(1)}
                      className={pagerBtnClass(currentPage === 1)}
                    >
                      1
                    </button>
                    {getVisiblePages()[0] > 2 && (
                      <span className="px-1 text-sm opacity-70">...</span>
                    )}
                  </>
                )}

                {getVisiblePages().map((page) => (
                  <button
                    key={page}
                    type="button"
                    onClick={() => goToPage(page)}
                    className={pagerBtnClass(page === currentPage)}
                  >
                    {page}
                  </button>
                ))}

                {getVisiblePages().at(-1) < totalPages && (
                  <>
                    {getVisiblePages().at(-1) < totalPages - 1 && (
                      <span className="px-1 text-sm opacity-70">...</span>
                    )}
                    <button
                      type="button"
                      onClick={() => goToPage(totalPages)}
                      className={pagerBtnClass(currentPage === totalPages)}
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  type="button"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={pagerBtnClass(false, currentPage === totalPages)}
                  title="Next"
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleTypeMaintenanceListing;